import {
	GET_NOTES,
	SAVE_NOTE,
	DELETE_NOTE,
	CREATE_NOTE,
	SET_WORKING_NOTE,
	SET_SHOW_WORKING_NOTE,
	REMOVE_NOTE,
	SET_SHARED_WITH,
	SET_NOTES_DRAWER_OPEN,
	SET_SHOW_WORKING_ALL_NOTE,
	SET_WORKING_ALL_NOTE,
	ADD_NOTE_TO_MEETING,
	SET_SHARED_WITH_GLOBAL
} from '../actions/notes';
import { handle } from 'redux-pack';
import { decodeHtmlEntities, uniqueArray } from '../../utils/utils';

interface Action {
	type: string;
	payload: any;
}

export interface Note {
	id: number | string;
	title: string;
	content: any;
	created_date?: number;
	edited_date?: number;
	created_by?: number;
	edited_by?: number;
	meeting: number;
	content_text: string;
	shared_with: string | null;
	readOnly?: boolean;
	meetings: number[];
}

export interface NotesReducerState {
	notes: Note[];
	workingNote: Note | null;
	workingAllNote: Note | null;
	loadingNotes: boolean;
	notesLoaded: boolean;
	notesError: string;
	savingNote: boolean;
	savingNoteError: string,
	deletingNote: boolean,
	deletingNoteError: string;
	deletedNotes: Note[];
	showWorkingNote: boolean;
	showWorkingAllNote: boolean;
	notesDrawerOpen: boolean;
}

export const initialState: NotesReducerState = {
	notes: [],
	workingNote: null,
	workingAllNote: null,
	loadingNotes: false,
	notesLoaded: false,
	notesError: "",
	savingNote: false,
	savingNoteError: '',
	deletingNote: false,
	deletingNoteError: '',
	deletedNotes: [],
	showWorkingNote: false,
	showWorkingAllNote: false,
	notesDrawerOpen: false
}

const actions: Map<any, Function> = new Map([
	[GET_NOTES, (state: NotesReducerState, action: Action) => handle(state, action, {
		start: state => ({ ...state, loadingNotes: true }),
		finish: state => ({ ...state, loadingNotes: false }),
		failure: state => ({ ...state, notesError: "Error loading notes. Please try again later." }),
		success: state => {
			const notes = [...action.payload].map((note) => {
				const { content, title } = note;
				return { ...note, title: decodeHtmlEntities(title), content: decodeHtmlEntities(content) };
			});
			return ({ ...state, notesError: "", notes: uniqueArray('id', notes), notesLoaded: true })
		}
	})],
	[SAVE_NOTE, (state: NotesReducerState, action: Action) => handle(state, action, {
		start: state => ({ ...state, savingNote: true }),
		finish: state => ({ ...state, savingNote: false }),
		failure: state => ({ ...state, savingNoteError: "Error saving note. Please try again." }),
		success: state => {
			const newState = { ...state };

			if (newState.notes.some((note: Note) => note.id === action.payload.id)) {
				let notes = newState.notes.map((note: Note) => {
					if (action.payload.id === note.id) {
						return {
							...action.payload,
							title: decodeHtmlEntities(action.payload.title),
							content: decodeHtmlEntities(action.payload.content),
							content_text: decodeHtmlEntities(action.payload.content_text),
						};
					} else {
						return note;
					}
				});

				newState.notes = notes;

				if (newState.workingNote && newState.workingNote.id === action.payload.id) {
					newState.workingNote = action.payload;
				}

				return newState;
			} else {
				newState.workingNote = action.payload;

				return { ...newState, notes: [action.payload, ...newState.notes] }
			}
		}
	})],
	[REMOVE_NOTE, (state: NotesReducerState, action: Action) => {
		return { ...state, notes: state.notes.filter((note: Note) => note.id !== action.payload) }
	}],
	[DELETE_NOTE, (state: NotesReducerState, action: Action) => handle(state, action, {
		start: state => ({ ...state, deletingNote: true }),
		finish: state => ({ ...state, deletingNote: false }),
		failure: state => ({ ...state, deletingNoteError: "Unable to delete note. Please try again." }),
		success: state => ({
			...state,
			deletingNoteError: '',
			deletedNotes: [...state.deletedNotes, state.notes.find((note: Note) => note.id === action.payload)!]
		})
	})],
	[CREATE_NOTE, (state: NotesReducerState, action: Action) => handle(state, action, {
		start: state => ({ ...state, savingNote: true }),
		finish: state => ({ ...state, savingNote: false }),
		failure: state => ({ ...state, savingNoteError: "Error saving note. Please try again." }),
		success: state => {
			const [newNote, fromGlobal] = action.payload;

			if (fromGlobal) {
				return {
					...state,
					workingAllNote: newNote
				}
			} else {
				return {
					...state,
					workingNote: newNote
				}
			}
		}
	})],
	[SET_WORKING_NOTE, (state: NotesReducerState, action: Action) => ({ ...state, workingNote: action.payload })],
	[SET_WORKING_ALL_NOTE, (state: NotesReducerState, action: Action) => ({ ...state, workingAllNote: action.payload })],
	[SET_SHOW_WORKING_ALL_NOTE, (state: NotesReducerState, action: Action) => ({ ...state, showWorkingAllNote: action.payload })],
	[SET_SHOW_WORKING_NOTE, (state: NotesReducerState, action: Action) => ({ ...state, showWorkingNote: action.payload })],
	[SET_SHARED_WITH, (state: NotesReducerState, action: Action) => ({ ...state, workingNote: { ...state.workingNote, shared_with: action.payload } })],
	[SET_SHARED_WITH_GLOBAL, (state: NotesReducerState, action: Action) => ({ ...state, workingAllNote: { ...state.workingAllNote, shared_with: action.payload } })],
	[SET_NOTES_DRAWER_OPEN, (state: NotesReducerState, action: Action) => ({ ...state, notesDrawerOpen: action.payload })],
	[ADD_NOTE_TO_MEETING, (state: NotesReducerState, action: Action) => handle(state, action, {
		success: state => {
			const { id, meeting } = action.payload;
			const notes = state.notes.map((note: Note) => {
				if (note.id === id) {
					note.meetings = [meeting, ...note.meetings];
				}

				return note;
			});

			return { ...state, notes }
		},
		failure: state => {
			return state;
		}
	})]
]);

export function NotesReducer(state: any = initialState, action: Action) {
	return actions.get(action.type)?.(state, action) || state;
}